import { useEffect, useState } from 'react';

// Shared timing helpers for the kitchen kiosk.
//
// The kiosk display powers off on its own standby schedule, which drops the
// HDMI output and makes Chromium treat the page as hidden. While hidden, the
// browser throttles — and after a long sleep effectively freezes —
// `setInterval` timers, so a card's polling loop silently stops and its data
// stays pinned to whenever the screen last went dark. The card looks "stale
// for a long time" until something forces a reload.
//
// The fix is to resync the moment the page comes back to the foreground:
// `visibilitychange` (screen wakes) and `focus` (tab regains focus) both fire
// on wake, so we re-run the tick immediately instead of waiting out the (now
// frozen) interval. Use `pollWithWake` for data-fetching loops and `useNow`
// for the clock-style label tickers.

/**
 * Run `tick` every `intervalMs`, AND immediately whenever the kiosk display
 * wakes / the tab regains focus. Returns a cleanup that clears the interval and
 * removes the wake listeners — call it from a `useEffect` cleanup.
 *
 * Drop-in for a bare `const t = setInterval(tick, ms); return () => clearInterval(t);`
 * inside an effect: `const stop = pollWithWake(tick, ms); return () => stop();`
 */
export function pollWithWake(tick: () => void, intervalMs: number): () => void {
  const t = setInterval(tick, intervalMs);
  const onWake = () => {
    if (document.visibilityState === 'visible') tick();
  };
  document.addEventListener('visibilitychange', onWake);
  window.addEventListener('focus', onWake);
  return () => {
    clearInterval(t);
    document.removeEventListener('visibilitychange', onWake);
    window.removeEventListener('focus', onWake);
  };
}

/**
 * A `Date` that re-renders every `intervalMs` and snaps back to real time the
 * moment the screen wakes — so time-of-day labels (Today/Tomorrow, the
 * current-hour marker, midnight rollover) stay correct without reloading the
 * kiosk, even after the display has been asleep.
 */
export function useNow(intervalMs: number): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const stop = pollWithWake(tick, intervalMs);
    return stop;
  }, [intervalMs]);
  return now;
}
